import { Platform } from "react-native";
import {
  DynamicSuccessConfig,
  ProcessedSection,
  ProcessedField,
} from "./DynamicSuccessPopUp.types";
import { formatNumberWithPrecision } from "@utils/product/productFormatters";
import { renderDateTime } from "@scenes/requests/utils/RequestsScreen";

export const getValueFromPath = (obj: any, path: string): any => {
  if (!obj || !path) return undefined;

  return path.split(".").reduce((acc, key) => {
    return acc?.[key];
  }, obj);
};

export const formatKey = (key: string): string => {
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

export const processConfigIntoSections = (
  config: DynamicSuccessConfig | undefined,
  successData: any
): ProcessedSection[] => {
  if (!config || !successData) return [];

  const sections: ProcessedSection[] = [];

  Object.entries(config).forEach(([sectionKey, sectionConfig]) => {
    const fields: ProcessedField[] = [];

    if (sectionConfig.options) {
      Object.entries(sectionConfig.options).forEach(
        ([dataSourceKey, fieldsConfig]) => {
          const dataSource = getValueFromPath(successData, dataSourceKey);

          if (dataSource) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            Object.entries(fieldsConfig).forEach(([fieldKey, fieldConfig]) => {
              const value = dataSource[fieldConfig.key];

              if (value !== undefined && value !== null && value !== "") {
                fields.push({
                  key: `${dataSourceKey}.${fieldConfig.key}`,
                  label: fieldConfig.label || formatKey(fieldConfig.key),
                  value,
                  order: fieldConfig.order,
                });
              }
            });
          }
        }
      );
    }

    if (sectionKey === "order_section" && successData.order_id) {
      const orderIdExists = fields.some((field) => field.key === "order_id");
      if (!orderIdExists) {
        const orderIdConfig = sectionConfig.options?.order_id;
        const orderValue = orderIdConfig?.order || 3;

        fields.push({
          key: "order_id",
          label: orderIdConfig?.label || "Order ID",
          value: successData.order_id,
          order: orderValue,
        });
      }
    }

    fields.sort((a, b) => a.order - b.order);

    if (fields.length > 0) {
      sections.push({
        key: sectionKey,
        label: sectionConfig.label,
        secondaryLabel: sectionConfig.secondary_label,
        order: sectionConfig.order,
        fields,
      });
    }
  });

  return sections.sort((a, b) => a.order - b.order);
};

export const getSectionLabel = (
  label: string | undefined,
  sectionKey: string,
  t: (key: string) => string
): string => {
  if (!label) {
    return formatKey(sectionKey);
  }

  if (label.includes("{{") || label.includes(".")) {
    const translationKey = label.replace(/{{|}}/g, "");
    return t(translationKey);
  }

  return label;
};

export const formatFieldValue = (key: string, value: any): string => {
  if (value === undefined || value === null) return "";

  if (key.includes("created_at")) {
    return renderDateTime(new Date(), "DD MMM, y [at] hh:mm A");
  }

  if (key.includes("date") || key.includes("updated_at")) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
    } catch {}
  }

  if (
    key.includes("amount") ||
    key.includes("price") ||
    key.includes("total")
  ) {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return formatNumberWithPrecision(num, { precision: 2 }) ?? "0.00";
    }
  }

  if (typeof value === "object") {
    if (value.address || value.city || value.state) {
      const parts = [
        value.address,
        value.city,
        value.state,
        value.zip,
        value.country,
      ].filter(Boolean);
      return parts.join(", ");
    }
    return JSON.stringify(value);
  }

  return String(value);
};

export const extractContactSection = (
  config: DynamicSuccessConfig | undefined,
  successData: any
): ProcessedSection | null => {
  if (!config?.contact_us || !successData) return null;

  const contactConfig = config.contact_us;
  const fields: ProcessedField[] = [];

  const contactSource =
    successData.seller || successData.contact || successData;

  if (contactConfig.options?.contact_email) {
    Object.entries(contactConfig.options.contact_email).forEach(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ([fieldKey, fieldConfig]) => {
        const value = contactSource[fieldConfig.key];
        if (value) {
          fields.push({
            key: fieldConfig.key,
            label: fieldConfig.label || formatKey(fieldConfig.key),
            value,
            order: fieldConfig.order,
          });
        }
      }
    );
  }

  if (fields.length === 0) return null;

  fields.sort((a, b) => a.order - b.order);

  return {
    key: "contact_us",
    label: contactConfig.label || "Questions about your order? Contact us:",
    secondaryLabel: contactConfig.secondary_label,
    order: contactConfig.order || 1,
    fields,
  };
};

export const extractShippingInformationSection = (
  config: DynamicSuccessConfig | undefined,
  successData: any
): {
  title: string;
  description: string;
  descriptionHeight: number;
  order: number;
} | null => {
  if (!config?.shipping_information) return null;

  const shippingConfig = config.shipping_information;

  // Get shipping instructions from API response if available, otherwise fallback to config
  const shippingDescription =
    successData?.shipping_instructions ||
    successData?.order?.shipping_instructions ||
    shippingConfig?.description;

  if (!shippingConfig.title || !shippingDescription) return null;

  return {
    title: shippingConfig?.title ?? "",
    description: shippingDescription ?? "",
    descriptionHeight: shippingConfig?.description_height || 76,
    order: shippingConfig.order || 3,
  };
};

export const parseWireTransferDetails = (
  wireTransferInstructions: string | undefined
): string | null => {
  if (!wireTransferInstructions) return null;

  try {
    const parsedData = JSON.parse(wireTransferInstructions);

    let platformKey: string;

    if (Platform.OS === "web") {
      platformKey = "webapp";
    } else if (Platform.OS === "ios") {
      platformKey = "ios";
    } else if (Platform.OS === "android") {
      platformKey = "android";
    } else {
      platformKey = "email";
    }

    return parsedData[platformKey] || parsedData.email || null;
  } catch (error) {
    console.error("Error parsing wire transfer details:", error);

    return wireTransferInstructions;
  }
};

export const extractWireTransferSection = (
  config: DynamicSuccessConfig | undefined,
  wireTransferInstructions: string | undefined
): {
  title: string;
  description: string;
  descriptionHeight: number;
  order: number;
} | null => {
  if (!config?.wire_transfer) return null;

  const wireTransferConfig = config.wire_transfer;

  if (!wireTransferConfig.displaySection) return null;

  const wireTransferHtml = parseWireTransferDetails(wireTransferInstructions);

  if (!wireTransferHtml) return null;

  return {
    title: wireTransferConfig.label || "orderCart.successPopUp.wireTransfer",
    description: wireTransferHtml,
    descriptionHeight: wireTransferConfig.description_height || 200,
    order: wireTransferConfig.order || 4,
  };
};
